import { useState, useRef, useEffect } from 'react';
import { Box, Center, Portal, Show } from '@chakra-ui/react'; // unnecessary import from @halo/ui-react, because it's not have styles
import { useUpdateEffect } from 'ahooks';
import { useZoom } from './ZoomController';

export function ZoomLevel() {
  const [open, setOpen] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>();
  const zoomTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { zoom, initialZoom } = useZoom();

  useUpdateEffect(() => {
    // Purpose: Clear timeout when zoom becomes null or undefined
    // Reason: Ensure clean state when zoom is reset
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
      zoomTimeoutRef.current = null;
    }

    // Purpose: Handle zoom reset (null or undefined)
    // Reason: Clean up state when zoom is cleared
    if (zoom === undefined || zoom === null) {
      setOpen(false);
      setZoomLevel(undefined);
      return;
    }

    // Purpose: Update zoom level and show indicator
    // Reason: Display current zoom percentage when zoom changes
    const zoomPercent = Math.floor(initialZoom * zoom * 100);
    setZoomLevel(zoomPercent);
    setOpen(true);

    // Purpose: Auto-hide after 2 seconds
    // Reason: Zoom indicator should disappear after a delay
    zoomTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      zoomTimeoutRef.current = null;
    }, 2_000);
  }, [zoom, initialZoom]);

  useEffect(() => {
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
        zoomTimeoutRef.current = null;
      }
    };
  }, []);

  const animationState = open ? 'open' : 'closed';

  return (
    <Show when={zoomLevel !== undefined}>
      <Portal>
        <Center
          position="fixed"
          top={0}
          left={0}
          w="100vw"
          h="100vh"
          pointerEvents="none"
          zIndex={9999}
        >
          <Box
            bg="blackAlpha.20"
            color="white"
            p="2"
            borderRadius="full"
            fontSize="sm"
            fontWeight="medium"
            minW="8"
            data-state={animationState}
            _open={{
              animationName: 'fade-in, scale-in',
              animationDuration: '300ms',
            }}
            _closed={{
              animationName: 'fade-out, scale-out',
              animationDuration: '120ms',
              animationFillMode: 'forwards',
            }}
          >
            {`${zoomLevel}%`}
          </Box>
        </Center>
      </Portal>
    </Show>
  );
}
